/**
 * ShopDiscountDialog - Create discount code dialog for Shop administration
 * Extracted from AdminShopTab.js in Phase 4D-6
 * 
 * Features:
 * - Create new discount codes (no edit functionality)
 * - Percentage or fixed-value discounts
 * - Optional: minimum order amount, max uses, expiry date
 * - Active/inactive toggle on creation
 * 
 * Note: Edit functionality does not exist in the original implementation
 */
import React from 'react';
import { Tag, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

/**
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Object} props.discountForm - Form state object
 * @param {string} props.discountForm.code - Discount code (uppercase)
 * @param {string} props.discountForm.discount_type - 'percentage' or 'fixed'
 * @param {string} props.discountForm.discount_value - Discount value
 * @param {string} props.discountForm.min_order_amount - Minimum order amount (optional)
 * @param {string} props.discountForm.max_uses - Maximum uses (optional)
 * @param {string} props.discountForm.expires_at - Expiry date YYYY-MM-DD (optional)
 * @param {boolean} props.discountForm.is_active - Whether discount is active
 * @param {Function} props.onFormChange - Callback to update form state
 * @param {Function} props.onSubmit - Callback when Create button clicked
 * @param {boolean} props.saving - Whether form is being submitted
 */
export function ShopDiscountDialog({
  open,
  onOpenChange,
  discountForm,
  onFormChange,
  onSubmit,
  saving
}) {
  // Helper to update a single form field
  const updateField = (field, value) => {
    onFormChange(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-violet-600" />
            Create Discount Code
          </DialogTitle>
          <DialogDescription>
            Create a promotional code for customers to use at checkout
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Code Input */}
          <div>
            <Label htmlFor="discount-code">Code *</Label>
            <Input
              id="discount-code"
              value={discountForm.code}
              onChange={(e) => updateField('code', e.target.value.toUpperCase())}
              placeholder="e.g., SAVE20, WELCOME10"
              className="font-mono uppercase"
            />
            <p className="text-xs text-slate-500 mt-1">Customers will enter this at checkout</p>
          </div>

          {/* Type and Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="discount-type">Discount Type</Label>
              <Select 
                value={discountForm.discount_type}
                onValueChange={(v) => updateField('discount_type', v)}
              >
                <SelectTrigger id="discount-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="discount-value">Value *</Label>
              <Input
                id="discount-value"
                type="number"
                min="0"
                step="0.01"
                value={discountForm.discount_value}
                onChange={(e) => updateField('discount_value', e.target.value)}
                placeholder={discountForm.discount_type === 'percentage' ? '20' : '5.00'}
              />
            </div>
          </div>

          {/* Min Order and Max Uses */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="min-order">Min Order (£)</Label>
              <Input
                id="min-order"
                type="number"
                min="0"
                step="0.01"
                value={discountForm.min_order_amount}
                onChange={(e) => updateField('min_order_amount', e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div>
              <Label htmlFor="max-uses">Max Uses</Label>
              <Input
                id="max-uses"
                type="number"
                min="1"
                value={discountForm.max_uses}
                onChange={(e) => updateField('max_uses', e.target.value)}
                placeholder="Unlimited"
              />
            </div>
          </div>

          {/* Expiry Date */}
          <div>
            <Label htmlFor="expires-at">Expiry Date</Label>
            <Input
              id="expires-at"
              type="date"
              value={discountForm.expires_at}
              onChange={(e) => updateField('expires_at', e.target.value)}
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-2">
            <Switch
              id="discount-active"
              checked={discountForm.is_active}
              onCheckedChange={(v) => updateField('is_active', v)}
            />
            <Label htmlFor="discount-active">Active</Label>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Discount
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShopDiscountDialog;
